import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Star, Loader2, User, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Star Rating Component
const StarRating = ({ rating, onRatingChange, interactive = false, size = 'md' }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const sizeClass = size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-6 w-6' : 'h-5 w-5';

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          className={`${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}`}
          onClick={() => interactive && onRatingChange?.(star)}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
        >
          <Star
            className={`${sizeClass} ${
              (interactive ? hoverRating || rating : rating) >= star
                ? 'text-amber-400 fill-amber-400'
                : 'text-muted-foreground/30'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Individual Review Card
const ReviewCard = ({ review, onDelete, canDelete }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/reviews/${review.id}`);
      onDelete(review.id);
      toast.success('Review deleted');
    } catch (error) {
      toast.error('Failed to delete review');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="border-b last:border-b-0 py-4" data-testid={`review-${review.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <User className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-foreground">{review.user_name}</span>
              <span className="text-xs text-muted-foreground">{formatDate(review.created_at)}</span>
            </div>
            <StarRating rating={review.rating} size="sm" />
            {review.comment && (
              <p className="text-muted-foreground mt-2 text-sm">{review.comment}</p>
            )}
          </div>
        </div>
        {canDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 w-8"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </Button>
        )}
      </div>
    </div>
  );
};

// Review Form Component
const ReviewForm = ({ itemId, itemType, onReviewSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/reviews`, {
        item_type: itemType,
        item_id: itemId,
        rating,
        comment
      });
      toast.success('Review submitted!');
      onReviewSubmitted(response.data.review);
      setRating(0);
      setComment('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit review';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="review-form">
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Your Rating</label>
        <StarRating rating={rating} onRatingChange={setRating} interactive size="lg" />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Your Review (optional)</label>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Share your experience with this product..."
          rows={3}
          className="resize-none"
          data-testid="review-comment-input"
        />
      </div>
      <Button type="submit" disabled={submitting || rating === 0} className="btn-accent" data-testid="submit-review-btn">
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Submitting...
          </>
        ) : (
          'Submit Review'
        )}
      </Button>
    </form>
  );
};

// Main Review Section Component
export const ReviewSection = ({ itemId, itemType = 'product' }) => {
  const { isAuthenticated, user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average_rating: 0, total_reviews: 0 });
  const [canReview, setCanReview] = useState(false);
  const [reviewReason, setReviewReason] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
    if (isAuthenticated) {
      checkCanReview();
    }
  }, [itemId, itemType, isAuthenticated]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${itemType}/${itemId}`);
      setReviews(response.data.reviews || []);
      setStats({
        average_rating: response.data.average_rating,
        total_reviews: response.data.total_reviews
      });
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkCanReview = async () => {
    try {
      const response = await axios.get(`${API}/reviews/user/can-review/${itemType}/${itemId}`);
      setCanReview(response.data.can_review);
      setReviewReason(response.data.reason);
    } catch (error) {
      console.error('Failed to check review eligibility:', error);
    }
  };

  const handleReviewSubmitted = (newReview) => {
    setReviews(prev => [newReview, ...prev]);
    setStats(prev => ({
      total_reviews: prev.total_reviews + 1,
      average_rating: ((prev.average_rating * prev.total_reviews) + newReview.rating) / (prev.total_reviews + 1)
    }));
    setCanReview(false);
    setReviewReason('already_reviewed');
  };

  const handleReviewDeleted = (reviewId) => {
    const deletedReview = reviews.find(r => r.id === reviewId);
    setReviews(prev => prev.filter(r => r.id !== reviewId));
    if (deletedReview) {
      const newTotal = stats.total_reviews - 1;
      setStats(prev => ({
        total_reviews: newTotal,
        average_rating: newTotal > 0 
          ? ((prev.average_rating * prev.total_reviews) - deletedReview.rating) / newTotal 
          : 0
      }));
      // Allow user to review again
      setCanReview(true);
      setReviewReason(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <section className="mt-12 pt-8 border-t" data-testid="review-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="font-serif text-2xl font-bold text-foreground">Customer Reviews</h2>
          {stats.total_reviews > 0 && (
            <div className="flex items-center gap-2 mt-1">
              <StarRating rating={Math.round(stats.average_rating)} size="sm" />
              <span className="text-sm text-muted-foreground">
                {stats.average_rating.toFixed(1)} out of 5 ({stats.total_reviews} {stats.total_reviews === 1 ? 'review' : 'reviews'})
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Write a Review Section */}
      {isAuthenticated ? (
        canReview ? (
          <Card className="mb-8 bg-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle className="text-lg">Write a Review</CardTitle>
            </CardHeader>
            <CardContent>
              <ReviewForm itemId={itemId} itemType={itemType} onReviewSubmitted={handleReviewSubmitted} />
            </CardContent>
          </Card>
        ) : reviewReason === 'already_reviewed' ? (
          <Card className="mb-8 bg-muted/50">
            <CardContent className="py-4">
              <p className="text-muted-foreground text-center">You have already reviewed this item.</p>
            </CardContent>
          </Card>
        ) : reviewReason === 'not_purchased' ? (
          <Card className="mb-8 bg-muted/50">
            <CardContent className="py-4">
              <p className="text-muted-foreground text-center">Purchase this item to leave a review.</p>
            </CardContent>
          </Card>
        ) : null
      ) : (
        <Card className="mb-8 bg-muted/50">
          <CardContent className="py-4">
            <p className="text-muted-foreground text-center">
              <a href="/login" className="text-primary hover:underline">Sign in</a> to leave a review.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Reviews List */}
      {reviews.length > 0 ? (
        <div className="divide-y">
          {reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              onDelete={handleReviewDeleted}
              canDelete={user?.id === review.user_id || user?.is_admin}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground" data-testid="no-reviews">
          <Star className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
          <p>No reviews yet. Be the first to share your thoughts!</p>
        </div>
      )}
    </section>
  );
};
